import { useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";

import { AssignmentChipSection, type AssignmentItem } from "../components/AssignmentChipSection";

export interface TaakTeam {
  code: string;
  naam: string;
  actief: boolean;
}

/**
 * De teams waarin een gebruiker zit (Intake, Medisch, of een zelf gemaakt team als Management). Zo krijgt en ziet
 * hij de taken van zijn team. Zelfde opzet als de rollen: elke chip erbij of eraf stuurt de bekende set plus of min
 * dat ene team, en pas als de set geladen is.
 */
export function UserTeamsSection({ userId }: { userId: string }) {
  const t = useTranslate();
  const catalogus = useCustom<TaakTeam[]>({ url: "taak-teams", method: "get" });
  const toegekend = useCustom<{ teams: string[] }>({ url: `users/${userId}/teams`, method: "get" });
  const { mutate: bewaar, isLoading: bezig } = useCustomMutation();
  const [fout, setFout] = useState<string | null>(null);

  const teams = Array.isArray(catalogus.data?.data) ? catalogus.data!.data : null;
  const codes = Array.isArray(toegekend.data?.data?.teams) ? toegekend.data!.data.teams : null;
  const naam = (code: string) => teams?.find((x) => x.code === code)?.naam ?? code;
  const assigned: AssignmentItem<string>[] | null = codes?.map((c) => ({ id: c, label: naam(c) })) ?? null;
  const options: AssignmentItem<string>[] | null =
    teams === null || codes === null
      ? null
      : teams.filter((x) => !codes.includes(x.code)).map((x) => ({ id: x.code, label: x.naam }));

  function schrijf(nieuw: string[]) {
    setFout(null);
    bewaar(
      { url: `users/${userId}/teams`, method: "put", values: { teams: nieuw } },
      {
        onSuccess: () => void toegekend.refetch(),
        onError: () => setFout(t("userDetailView.teams.saveFailed")),
      },
    );
  }

  return (
    <AssignmentChipSection
      className="user-teams"
      title={t("userDetailView.teams.title")}
      assigned={assigned}
      options={options}
      isError={catalogus.isError || toegekend.isError}
      busy={bezig}
      actionError={fout}
      onAdd={(code) => schrijf([...(codes ?? []), code])}
      onRemove={(code) => schrijf((codes ?? []).filter((c) => c !== code))}
      labels={{
        loading: t("common.loading"),
        loadFailed: t("userDetailView.teams.loadFailed"),
        empty: t("userDetailView.teams.none"),
        add: t("userDetailView.teams.add"),
        addDialogTitle: t("userDetailView.teams.addTitle"),
        addDialogEmpty: t("userDetailView.teams.addNoneLeft"),
        removeTitle: t("userDetailView.teams.removeTitle"),
        removeBody: (label) => t("userDetailView.teams.removeBody").replace("%s", label),
        removeConfirm: t("common.remove"),
        cancel: t("common.cancel"),
        removeAria: (label) => t("userDetailView.teams.removeAria").replace("%s", label),
        searchPlaceholder: t("userDetailView.teams.searchPlaceholder"),
        searchNoMatches: t("common.noMatches"),
      }}
    />
  );
}
